'use client';

import React, { memo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Check, Copy, ChevronRight, X, Download, FileAudio, FileVideo, FileText, FileCode, FileArchive, File as FileIcon, Box, FolderOpen } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { getToolLabel, formatToolResult, getInteractiveResult, getTextEditorResult, getBashResult, getDiscoveryResult, shouldAutoExpand } from '@/components/tool-renderers';
import { useT } from '@/components/i18n-provider';
import Image from 'next/image';
import Link from 'next/link';
import { AudioPreview } from '@/components/content-previews';
import { LinkPreview, LinkBar } from '@/components/link-preview';
import { downloadContent } from '@/lib/content/download-client';
import { useChatHost } from '@/components/chat/chat-host-context';

const AUDIO_EXT_RE = /\.(?:mp3|wav|ogg|aac|opus|m4a|flac|webm)(?:\?|$)/i;
const VIDEO_EXT_RE = /\.(?:mp4|webm|mov)(?:\?|$)/i;
const IMAGE_EXT_RE = /\.(?:png|jpe?g|gif|webp|svg|bmp|ico|avif)(?:\?|$)/i;

function videoThumbnailSrc(src: string): string {
  if (src.includes('#')) return src;
  return src + '#t=0.001';
}

function childrenText(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(childrenText).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    return childrenText((children as React.ReactElement<{ children?: React.ReactNode }>).props.children);
  }
  return '';
}

function isAudioLink(url: string): boolean {
  return AUDIO_EXT_RE.test(url);
}

function isVideoLink(url: string): boolean {
  return VIDEO_EXT_RE.test(url);
}

function isImageLink(url: string): boolean {
  return IMAGE_EXT_RE.test(url);
}

function isBareUrl(url: string, children: React.ReactNode): boolean {
  const text = childrenText(children).trim();
  return text === url || (text.startsWith('http') && text.includes('://'));
}

// ---------------------------------------------------------------------------
// Clickable image with portal-based lightbox (avoids div-inside-p hydration)
// ---------------------------------------------------------------------------

function resolveImageSrc(src: string): string | null {
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  let resolved = UUID_RE.test(src) ? `/api/content/${src}` : src;
  if (!/^(\/|https?:\/\/|data:)/.test(resolved)) return null;

  const SELF_ORIGINS = ['https://robutler.ai', 'https://dev.robutler.net', 'https://staging.robutler.net'];
  for (const origin of SELF_ORIGINS) {
    if (resolved.startsWith(origin + '/')) {
      resolved = resolved.slice(origin.length);
      break;
    }
  }
  return resolved;
}

function ClickableImage({ src, alt }: { src?: string; alt?: string }) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  if (!src) return null;

  const resolved = resolveImageSrc(src);

  if (!resolved || errored) {
    return <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{`![${alt || ''}](${src})`}</code>;
  }

  return (
    <>
      <span className="relative inline-block rounded-lg overflow-hidden">
        {!loaded && (
          <span className="block w-full max-w-[720px] bg-muted animate-pulse rounded-lg" style={{ aspectRatio: '3/2' }} />
        )}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={resolved}
          alt={alt || ''}
          className={cn('rounded-lg max-w-full max-h-96 cursor-pointer hover:opacity-90 transition-opacity not-prose w-auto', loaded ? 'opacity-100' : 'opacity-0 absolute inset-0')}
          onClick={() => setOpen(true)}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
        />
      </span>
      {open && mounted && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90"
          onClick={() => setOpen(false)}
        >
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            <button
              onClick={(e) => { e.stopPropagation(); downloadContent({ url: resolved }); }}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={() => setOpen(false)}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={resolved}
            alt={alt || ''}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Code block with copy button
// ---------------------------------------------------------------------------

function CodeBlock({
  inline,
  className,
  children,
  ...props
}: {
  inline: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const text = String(children).replace(/\n$/, '');
  const lang = className?.replace('language-', '') ?? '';

  if (inline) {
    // Check for streaming indicator marker
    if (text.trim() === '[...]') return <InlineProgress />;
    return (
      <code className="px-1.5 py-0.5 rounded bg-muted font-mono text-[0.85em]" {...props}>
        {children}
      </code>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative group my-2 min-w-0 max-w-full overflow-hidden">
      {lang && (
        <div className="text-[10px] text-muted-foreground px-3 pt-2 font-mono uppercase tracking-wide">
          {lang}
        </div>
      )}
      <pre className="text-sm overflow-x-auto bg-muted/60 dark:bg-zinc-900 px-4 py-3 rounded-lg border border-border font-mono whitespace-pre-wrap break-words">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-background/80 border border-border text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:text-foreground"
        aria-label={t('tools.copyCode')}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared style tokens — used across ToolCallBlock + ThinkingBlock so
// streaming and non-streaming variants stay pixel-identical.
// ---------------------------------------------------------------------------

const TOOL_LABEL_CLS = 'text-xs text-muted-foreground';
const TOOL_BODY_CLS = 'text-xs text-foreground/55 dark:text-foreground/45 break-words whitespace-pre-wrap leading-relaxed';
const THINKING_BODY_CLS = 'text-[11px] text-foreground/45 dark:text-foreground/35 break-words whitespace-pre-wrap leading-relaxed';

const INLINE_FADE_STYLE: React.CSSProperties = {
  WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 1.25rem)',
  maskImage: 'linear-gradient(to bottom, transparent, black 1.25rem)',
};

function InlineScrollPanel({
  children,
  maxH = 'max-h-32',
  innerRef,
  className,
}: {
  children: React.ReactNode;
  maxH?: string;
  innerRef?: React.Ref<HTMLDivElement>;
  className?: string;
}) {
  return (
    <div
      ref={innerRef}
      className={cn(
        'scrollbar-hover-only overflow-y-auto overflow-x-hidden pt-2 pl-3 scroll-smooth min-w-0 max-w-full',
        maxH,
        className,
      )}
      style={INLINE_FADE_STYLE}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline streaming progress indicator
// ---------------------------------------------------------------------------

function InlineProgress() {
  return (
    <span className="inline-flex gap-0.5 ml-1 text-muted-foreground">
      {[0, 200, 400].map((delay) => (
        <span
          key={delay}
          className="w-1 h-1 rounded-full bg-current animate-bounce"
          style={{ animationDelay: `${delay}ms`, animationDuration: '1s' }}
        />
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Thinking / reasoning block (collapsible)
// ---------------------------------------------------------------------------

export function ThinkingBlock({
  content,
  isStreaming = false,
}: {
  content: string;
  isStreaming?: boolean;
}) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);
  const prevLen = useRef(0);
  const startTimeRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isStreaming) {
      setElapsed(Math.round((Date.now() - startTimeRef.current) / 1000));
      return;
    }
    const interval = setInterval(() => {
      setElapsed(Math.round((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isStreaming]);

  useEffect(() => {
    if (!isStreaming || !streamRef.current || !content) return;
    if (content.length <= prevLen.current) return;
    prevLen.current = content.length;
    const node = streamRef.current;
    const raf = requestAnimationFrame(() => {
      try {
        node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
      } catch {
        node.scrollTop = node.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [content, isStreaming]);

  useEffect(() => {
    if (!isStreaming && prevLen.current > 0) {
      const timer = setTimeout(() => {
        setExpanded(false);
        prevLen.current = 0;
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isStreaming]);

  if (isStreaming && content) {
    return (
      <div className="group/tool max-w-full">
        <div className={cn('flex items-center gap-1.5 py-0.5', TOOL_LABEL_CLS)}>
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-pulse shrink-0" />
          <span>{t('tools.thinking')}</span>
          {elapsed > 0 && (
            <span className="text-[10px] text-muted-foreground/50">{elapsed}s</span>
          )}
        </div>
        <InlineScrollPanel innerRef={streamRef} maxH="max-h-24">
          <div className={THINKING_BODY_CLS}>{content}</div>
        </InlineScrollPanel>
      </div>
    );
  }

  return (
    <div className="group/tool max-w-full">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className={cn(
          'flex items-center gap-1.5 py-0.5 hover:text-foreground transition-colors min-w-0 max-w-full',
          TOOL_LABEL_CLS,
        )}
      >
        <span>{t('tools.thoughtProcess')}</span>
        {elapsed > 0 && (
          <span className="text-[10px] text-muted-foreground/50">{elapsed}s</span>
        )}
        <ChevronRight
          className={cn(
            'h-3 w-3 shrink-0 transition-all duration-200',
            expanded ? 'rotate-90 opacity-100' : 'opacity-0 group-hover/tool:opacity-60'
          )}
        />
      </button>
      {expanded && (
        <div className="mt-1 mb-1 animate-in fade-in-0 slide-in-from-top-1 duration-150 min-w-0">
          <InlineScrollPanel maxH="max-h-60">
            <div className={cn(THINKING_BODY_CLS, 'font-mono')}>
              {(content?.replace(/\s+$/, '')) || t('tools.noContent')}
            </div>
          </InlineScrollPanel>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NLI progress text formatting
// ---------------------------------------------------------------------------

interface AttachmentInfo {
  contentType?: 'image' | 'video' | 'audio' | 'document' | 'model3d' | 'folder' | string;
  mimeType?: string;
  displayName?: string;
}

const attachmentInfoCache = new Map<string, AttachmentInfo | null>();

function pickAttachmentIcon(info: AttachmentInfo | null): React.ComponentType<{ className?: string }> {
  if (!info) return FileIcon;
  const ct = info.contentType;
  const mime = info.mimeType ?? '';
  if (ct === 'folder') return FolderOpen;
  if (ct === 'audio' || mime.startsWith('audio/')) return FileAudio;
  if (ct === 'video' || mime.startsWith('video/')) return FileVideo;
  if (ct === 'model3d') return Box;
  if (ct === 'image' || mime.startsWith('image/')) return FileIcon;
  if (mime === 'application/pdf') return FileText;
  if (mime === 'application/zip' || mime === 'application/x-tar' || mime === 'application/gzip') return FileArchive;
  if (
    mime.startsWith('text/x-')
    || mime === 'text/javascript'
    || mime === 'application/json'
    || mime === 'application/xml'
    || mime === 'application/javascript'
    || mime === 'application/x-javascript'
    || mime === 'application/x-typescript'
    || mime === 'application/x-sh'
    || mime === 'application/x-yaml'
    || mime === 'application/yaml'
  ) return FileCode;
  if (mime.startsWith('text/')) return FileText;
  return FileIcon;
}

export function AttachmentThumb({ id }: { id: string }) {
  const [info, setInfo] = useState<AttachmentInfo | null | undefined>(() => attachmentInfoCache.get(id));
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (info !== undefined) return;
    let cancelled = false;
    void (async () => {
      try {
        const resp = await fetch(`/api/content/${id}/info`, { credentials: 'include' });
        if (!resp.ok) {
          if (!cancelled) {
            attachmentInfoCache.set(id, null);
            setInfo(null);
          }
          return;
        }
        const data = (await resp.json()) as AttachmentInfo;
        if (!cancelled) {
          attachmentInfoCache.set(id, data);
          setInfo(data);
        }
      } catch {
        if (!cancelled) {
          attachmentInfoCache.set(id, null);
          setInfo(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, info]);

  const isImage = info && (info.contentType === 'image' || (info.mimeType ?? '').startsWith('image/'));

  if (isImage && !imgFailed) {
    return (
      <Image
        src={`/api/content/${id}`}
        alt={info?.displayName ?? ''}
        title={info?.displayName ?? id}
        width={32}
        height={32}
        unoptimized
        className="h-8 w-8 rounded object-cover border border-border/50"
        sizes="32px"
        onError={() => setImgFailed(true)}
      />
    );
  }

  const Icon = pickAttachmentIcon(info ?? null);
  return (
    <span
      className="flex items-center justify-center h-8 w-8 rounded border border-border/50 bg-muted/40"
      title={info?.displayName ?? id}
    >
      <Icon className="h-4 w-4 text-muted-foreground/60" />
    </span>
  );
}

export function FormatProgressLines({ text }: { text: string }) {
  const trimmed = text.replace(/\n{3,}/g, '\n\n').trim();
  if (!trimmed) return null;
  return <span className={TOOL_BODY_CLS}>{trimmed}</span>;
}

function NliRequestPreview({ text }: { text: string }) {
  return (
    <div className="flex gap-2 min-w-0 pl-3 mt-0.5">
      <div className="w-0.5 shrink-0 rounded-full bg-muted-foreground/20" />
      <div className="scrollbar-hover-only min-w-0 max-h-24 overflow-y-auto">
        <p className="text-[11px] text-muted-foreground/40 leading-relaxed break-words whitespace-pre-wrap">
          {text}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tool call block (collapsible)
// ---------------------------------------------------------------------------

export function ToolCallBlock({
  name,
  args,
  result,
  data,
  isError,
  isStreaming,
  progressKey,
  progressSegments,
  onApprovalAction,
}: {
  name: string;
  args?: string;
  result?: string;
  /** Optional structured metadata returned by the tool (e.g. `{ subChatId }`
   *  for NLI/delegate). */
  data?: Record<string, unknown>;
  isError?: boolean;
  isStreaming?: boolean;
  progressKey?: string;
  progressSegments?: Array<{ kind: 'append' | 'replace' | 'thinking'; text: string }>;
  onApprovalAction?: (action: string, data: unknown) => void;
}) {
  const t = useT();
  const isNliTool = /^(nli|delegate)/.test(name.replace(/_tool$/, ''));
  const isMediaGen = /^generate_(image|video|audio|3d)/.test(name.replace(/_tool$/, ''));
  const autoExpand = isNliTool || shouldAutoExpand(name);
  const [expanded, setExpanded] = useState(autoExpand && !result);
  const streamRef = useRef<HTMLDivElement>(null);
  const prevResultLen = useRef(0);
  const wasStreamingRef = useRef(isStreaming);

  const nliStartRef = useRef(Date.now());
  const [nliElapsed, setNliElapsed] = useState(0);

  useEffect(() => {
    if (!isNliTool) return;
    if (!isStreaming) {
      setNliElapsed(Math.round((Date.now() - nliStartRef.current) / 1000));
      return;
    }
    const iv = setInterval(() => setNliElapsed(Math.round((Date.now() - nliStartRef.current) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [isStreaming, isNliTool]);

  const interactiveUI = (result || data || args) ? getInteractiveResult(t, name, result ?? '', onApprovalAction, data, args, isStreaming, isError) : null;
  const textEditorUI = !interactiveUI && !isError ? getTextEditorResult(t, name, args, result) : null;
  const bashUI = !interactiveUI && !textEditorUI && !isError ? getBashResult(t, name, args, result) : null;
  const discoveryUI = !interactiveUI && !textEditorUI && !bashUI && result && !isError ? getDiscoveryResult(t, name, result) : null;
  const formattedResult = result && !isError && !interactiveUI && !textEditorUI && !bashUI && !discoveryUI ? formatToolResult(t, name, result) : null;
  const label = getToolLabel(t, name, args, result, isStreaming);

  const nliAgent = isNliTool ? (() => {
    try { const p = args ? JSON.parse(args) : {}; return (p.agent || p.agent_url || p.agent_identifier) as string | undefined; } catch { return undefined; }
  })() : undefined;
  const nliLink = isNliTool && nliAgent ? (active: boolean) => (
    <>
      {isStreaming ? t('tools.talkingTo') : t('tools.talkedTo')}{' '}
      <Link
        href={`/u/${nliAgent.replace(/^@/, '')}`}
        className={cn(
          'transition-colors',
          active ? 'text-purple-400' : 'hover:text-purple-400'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        @{nliAgent.replace(/^@/, '')}
      </Link>
    </>
  ) : null;

  // Extract request message and attachments from args for NLI/delegate
  const nliRequest = isNliTool ? (() => {
    try { const p = args ? JSON.parse(args) : {}; return (p.message as string) || undefined; } catch { return undefined; }
  })() : undefined;
  const nliAttachments = isNliTool ? (() => {
    try {
      const p = args ? JSON.parse(args) : {};
      if (!Array.isArray(p.attachments)) return undefined;
      return (p.attachments as string[]).map((a) => {
        const m = a.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
        return m ? m[1] : a;
      });
    } catch { return undefined; }
  })() : undefined;

  // Auto-scroll the streaming container
  useEffect(() => {
    if (!isStreaming || !streamRef.current || !result) return;
    if (result.length > prevResultLen.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
      prevResultLen.current = result.length;
    }
  }, [result, isStreaming]);

  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && autoExpand) {
      const delay = isNliTool ? 800 : 1500;
      const timer = setTimeout(() => setExpanded(false), delay);
      wasStreamingRef.current = isStreaming;
      return () => clearTimeout(timer);
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, autoExpand, isNliTool]);

  // NLI streaming: header → request → attachments → segments interleaved chronologically.
  // Single inline column, no nested panels, no borders. Thinking and text/progress
  // segments are rendered in arrival order and share the delegate column (pl-3).
  if (isStreaming && isNliTool) {
    const segs = progressSegments ?? [];
    const hasAnySegs = segs.length > 0;
    const lastIdx = segs.length - 1;
    return (
      <div className="group/tool min-w-0 max-w-full">
        <div className={cn('flex items-center gap-1.5 py-0.5 min-w-0 max-w-full', TOOL_LABEL_CLS)}>
          <span className="h-1.5 w-1.5 rounded-full bg-purple-400 animate-pulse shrink-0" />
          <span className="truncate">{nliLink?.(true) || label}</span>
          {nliElapsed > 0 && (
            <span className="text-[10px] text-muted-foreground/50">{nliElapsed}s</span>
          )}
        </div>
        {nliRequest && <NliRequestPreview text={nliRequest} />}
        {nliAttachments && nliAttachments.length > 0 && (
          <div className="flex gap-1 flex-wrap pl-3 mt-1">
            {nliAttachments.map((id) => (
              <AttachmentThumb key={id} id={id} />
            ))}
          </div>
        )}
        {hasAnySegs ? (
          <div className="pl-3 min-w-0 max-w-full">
            {segs.map((seg, i) => {
              if (seg.kind === 'thinking') {
                return (
                  <ThinkingBlock
                    key={`seg-${i}`}
                    content={seg.text}
                    isStreaming={i === lastIdx}
                  />
                );
              }
              return (
                <InlineScrollPanel key={`seg-${i}`} innerRef={i === lastIdx ? streamRef : undefined} maxH="max-h-32">
                  <FormatProgressLines text={seg.text} />
                </InlineScrollPanel>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 pl-3 pt-1 text-muted-foreground/40">
            <Spinner size="xs" />
            <span className="text-xs">{t('tools.waitingForResponse')}</span>
          </div>
        )}
      </div>
    );
  }

  if (isStreaming && !result && !isNliTool && !isMediaGen) {
    return (
      <div className="group/tool max-w-full">
        <div className={cn('flex items-center gap-1.5 py-0.5', TOOL_LABEL_CLS)}>
          <Spinner size="xs" />
          <span className="truncate">{label}</span>
        </div>
      </div>
    );
  }

  // NLI completed: header → request preview → (collapsed) thinking; expanded shows result inline.
  if (isNliTool) {
    return (
      <div className="group/tool min-w-0 max-w-full">
        <button
          type="button"
          onClick={() => setExpanded((p) => !p)}
          className={cn(
            'flex items-center gap-1.5 py-0.5 hover:text-foreground transition-colors min-w-0 max-w-full',
            TOOL_LABEL_CLS,
          )}
        >
          <span className="truncate">{nliLink?.(expanded) || label}</span>
          {nliElapsed > 0 && (
            <span className="text-[10px] text-muted-foreground/50">{nliElapsed}s</span>
          )}
          {isError && <span className="text-[10px] text-red-500 font-medium shrink-0">{t('tools.error')}</span>}
          <ChevronRight
            className={cn(
              'h-3 w-3 shrink-0 transition-all duration-200',
              expanded ? 'rotate-90 opacity-100' : 'opacity-0 group-hover/tool:opacity-60'
            )}
          />
        </button>
        {nliRequest && expanded && <NliRequestPreview text={nliRequest} />}
        {nliAttachments && nliAttachments.length > 0 && expanded && (
          <div className="flex gap-1 flex-wrap pl-3 mt-1">
            {nliAttachments.map((id) => (
              <AttachmentThumb key={id} id={id} />
            ))}
          </div>
        )}
        {expanded && (
          <div className="mt-1 animate-in fade-in-0 slide-in-from-top-1 duration-150 min-w-0">
            {interactiveUI ? (
              <div className="pl-3">{interactiveUI}</div>
            ) : discoveryUI ? (
              <InlineScrollPanel maxH="max-h-60">
                <div className={cn(TOOL_BODY_CLS, 'min-w-0')}>{discoveryUI}</div>
              </InlineScrollPanel>
            ) : formattedResult ? (
              <InlineScrollPanel maxH="max-h-60">
                <div className={cn(TOOL_BODY_CLS, 'prose prose-sm dark:prose-invert max-w-none')}>
                  <Markdown>{formattedResult}</Markdown>
                </div>
              </InlineScrollPanel>
            ) : result ? (
              (() => {
                const trimmed = result.trim();
                if (!trimmed) return null;
                if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                  return (
                    <InlineScrollPanel maxH="max-h-60">
                      <pre
                        className={cn(
                          TOOL_BODY_CLS,
                          'font-mono break-all max-w-full',
                          isError && 'text-red-600 dark:text-red-400',
                        )}
                      >
                        {formatJSON(result)}
                      </pre>
                    </InlineScrollPanel>
                  );
                }
                return (
                  <InlineScrollPanel maxH="max-h-60">
                    <FormatProgressLines text={result} />
                  </InlineScrollPanel>
                );
              })()
            ) : (
              <div className={cn('pl-3', TOOL_BODY_CLS)}>{t('tools.noResponse')}</div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Media gen tools: expandable accordion showing generation parameters.
  // Media itself is rendered as first-class content items in the chat bubble.
  if (isMediaGen) {
    const genParams = (() => {
      try {
        const p = args ? JSON.parse(args) : {};
        const lines: string[] = [];
        if (p.prompt) lines.push(`**Prompt:** ${p.prompt}`);
        if (p.image_url) lines.push(`**Input image:** ${p.image_url}`);
        if (p.duration) lines.push(`**Duration:** ${p.duration}s`);
        if (p.aspect_ratio) lines.push(`**Aspect ratio:** ${p.aspect_ratio}`);
        if (p.image_size) lines.push(`**Size:** ${p.image_size}`);
        if (p.num_images && p.num_images > 1) lines.push(`**Count:** ${p.num_images}`);
        if (p.seed !== undefined) lines.push(`**Seed:** ${p.seed}`);
        return lines.length > 0 ? lines.join('\n\n') : null;
      } catch { return null; }
    })();

    return (
      <div className="group/tool min-w-0 max-w-full">
        <button
          type="button"
          onClick={() => setExpanded((p) => !p)}
          className={cn(
            'flex items-center gap-1.5 py-0.5 hover:text-foreground transition-colors min-w-0 max-w-full',
            TOOL_LABEL_CLS,
          )}
        >
          {isStreaming && <Spinner size="xs" />}
          <span className="truncate">{label}</span>
          {isError && <span className="text-[10px] text-red-500 font-medium shrink-0">{t('tools.error')}</span>}
          <ChevronRight
            className={cn(
              'h-3 w-3 shrink-0 transition-all duration-200',
              expanded ? 'rotate-90 opacity-100' : 'opacity-0 group-hover/tool:opacity-60'
            )}
          />
        </button>
        {expanded && (
          <div className="mt-1 mb-1 animate-in fade-in-0 slide-in-from-top-1 duration-150 min-w-0">
            {genParams && (
              <InlineScrollPanel maxH="max-h-60">
                <div className={cn(TOOL_BODY_CLS, 'prose prose-sm dark:prose-invert max-w-none prose-p:my-0.5')}>
                  <Markdown>{genParams}</Markdown>
                </div>
              </InlineScrollPanel>
            )}
            {isStreaming && result && (
              <div className="flex items-center gap-1.5 mt-2 pl-3 text-muted-foreground/60">
                <Spinner size="xs" />
                <span
                  key={progressKey ?? result}
                  className="text-xs animate-in fade-in-0 slide-in-from-bottom-0.5 duration-300"
                >
                  {result}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="group/tool min-w-0 max-w-full">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className={cn(
          'flex items-center gap-1.5 py-0.5 hover:text-foreground transition-colors min-w-0 max-w-full',
          TOOL_LABEL_CLS,
        )}
      >
        {isStreaming && <Spinner size="xs" />}
        {!isStreaming && result && !isError && typeof result === 'string' && /^(Done|OK|Sent|Published|Disconnected)$/i.test(result.trim()) && (
          <Check className="h-3 w-3 text-green-500 shrink-0" />
        )}
        <span className="truncate">{label}</span>
        {isError && <span className="text-[10px] text-red-500 font-medium shrink-0">{t('tools.error')}</span>}
        <ChevronRight
          className={cn(
            'h-3 w-3 shrink-0 transition-all duration-200',
            expanded ? 'rotate-90 opacity-100' : 'opacity-0 group-hover/tool:opacity-60'
          )}
        />
      </button>
      {expanded && (
        <div className="mt-1 mb-1 animate-in fade-in-0 slide-in-from-top-1 duration-150 min-w-0">
          {interactiveUI ? (
            <div className="pl-3 min-w-0">{interactiveUI}</div>
          ) : textEditorUI ? (
            <InlineScrollPanel maxH="max-h-60">
              <div className="min-w-0">{textEditorUI}</div>
            </InlineScrollPanel>
          ) : bashUI ? (
            <InlineScrollPanel maxH="max-h-60">
              <div className="min-w-0">{bashUI}</div>
            </InlineScrollPanel>
          ) : discoveryUI ? (
            <InlineScrollPanel maxH="max-h-60">
              <div className={cn(TOOL_BODY_CLS, 'min-w-0')}>{discoveryUI}</div>
            </InlineScrollPanel>
          ) : formattedResult ? (
            <InlineScrollPanel maxH="max-h-60">
              <div className={cn(TOOL_BODY_CLS, 'prose prose-sm dark:prose-invert max-w-none')}>
                <Markdown>{formattedResult}</Markdown>
              </div>
            </InlineScrollPanel>
          ) : result ? (
            <InlineScrollPanel maxH="max-h-60">
              <pre
                className={cn(
                  TOOL_BODY_CLS,
                  'font-mono break-all max-w-full',
                  isError && 'text-red-600 dark:text-red-400',
                )}
              >
                {formatJSON(result)}
              </pre>
            </InlineScrollPanel>
          ) : (
            <div className={cn('pl-3 py-1', TOOL_BODY_CLS)}>{t('tools.noResult')}</div>
          )}
        </div>
      )}
    </div>
  );
}

export function formatJSON(str: string | unknown): string {
  if (str != null && typeof str === 'object') {
    try { return JSON.stringify(str, null, 2); } catch { return String(str); }
  }
  if (typeof str !== 'string') return String(str);
  try { return JSON.stringify(JSON.parse(str), null, 2); } catch { /* continue */ }
  try {
    const fixed = str
      .replace(/'/g, '"')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')
      .replace(/\bNone\b/g, 'null');
    return JSON.stringify(JSON.parse(fixed), null, 2);
  } catch {
    return str;
  }
}

// ---------------------------------------------------------------------------
// react-markdown component overrides
// ---------------------------------------------------------------------------

/**
 * Renders a `/u/<handle>` mention link. When the surrounding host (an
 * AgentNode on the workspace canvas, today; potentially others) exposes
 * an `onOpenProfile` handler via `ChatHostContext`, clicks are routed
 * there and the default `Link` navigation is suppressed — letting the
 * host spawn a sibling participant widget instead of tearing the user
 * out of the workspace.
 *
 * Markdown is used in non-chat surfaces too (post bodies, profile bios,
 * docs, comments) where no `ChatHostProvider` is mounted. In that case
 * `useChatHost()` returns `{}` and we fall back to the original Next
 * `<Link>` — preserving prior behaviour. This is the only reason the
 * component exists as a small named React component instead of being
 * inlined in the `a:` renderer: `makeComponents` is created at module
 * scope and cannot call `useChatHost()` directly.
 */
function HostAwareLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  const host = useChatHost();
  const onClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.stopPropagation();
      const handler = host.onOpenProfile;
      if (!handler) return;
      // Only allow plain-left-click to be intercepted; modifier clicks
      // (cmd/ctrl/shift/middle) keep their default open-in-new-tab
      // behaviour so power users aren't surprised.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      const match = href.match(/^\/u\/([^/]+)$/);
      if (!match) return;
      const handle = decodeURIComponent(match[1]!);
      const maybe = handler(handle);
      // We can't await a Promise<true> before deciding whether to
      // navigate, so the contract is: a host that wants to claim the
      // click MUST be synchronous-true (it can still kick off async
      // work, it just has to commit to the spawn first). Anything else
      // falls through to default navigation.
      if (maybe === true) e.preventDefault();
    },
    [host, href],
  );
  return (
    <Link
      href={href}
      onClick={onClick}
      className="text-purple-500 dark:text-purple-400 hover:text-purple-400 dark:hover:text-purple-300 hover:underline transition-colors no-underline font-medium"
    >
      {children}
    </Link>
  );
}

function makeComponents(suppressLinkPreviews = false): Partial<Components> {
  return {
  pre: ({ children, ...props }) => (
    <pre {...props} className="not-prose">
      {children}
    </pre>
  ),
  // @ts-expect-error ReactMarkdown code signature
  code: ({ node, inline, className, children, ...props }) => (
    <CodeBlock inline={inline ?? !className} className={className || ''} {...props}>
      {children}
    </CodeBlock>
  ),
  p: ({ children, ...props }) => (
    <div {...props} className="my-1" style={{ whiteSpace: 'pre-line' }}>
      {children}
    </div>
  ),
  a: ({ children, href, ...props }) => {
    const url = href || '';
    // Internal @agent / @user mentions → purple link. When a chat host
    // (e.g. workspace AgentNode) supplies `onOpenProfile`, the click
    // spawns a sibling participant widget instead of navigating.
    if (/^\/u\/[^/]+$/.test(url)) {
      return <HostAwareLink href={url}>{children}</HostAwareLink>;
    }
    if (isAudioLink(url)) {
      return <AudioPreview src={url} className="my-1 max-w-full not-prose" />;
    }
    if (isVideoLink(url)) {
      return <video src={videoThumbnailSrc(url)} controls preload="metadata" className="my-1 max-w-full max-h-80 rounded-lg not-prose" />;
    }
    if (isImageLink(url)) {
      return (
        <span className="block">
          <ClickableImage src={url} alt={childrenText(children) || 'image'} />
          <LinkBar url={url} internal={false} />
        </span>
      );
    }
    if (isBareUrl(url, children) && !suppressLinkPreviews) {
      return <LinkPreview url={url}>{children}</LinkPreview>;
    }
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-blue-500 hover:underline break-all"
        {...props}
      >
        {children}
      </a>
    );
  },
  ol: ({ children, ...props }) => (
    <ol className="list-decimal list-outside ml-4" {...props}>{children}</ol>
  ),
  ul: ({ children, ...props }) => (
    <ul className="list-disc list-outside ml-4" {...props}>{children}</ul>
  ),
  li: ({ children, ...props }) => (
    <li className="py-0.5" {...props}>{children}</li>
  ),
  h1: ({ children, ...props }) => <h1 className="text-xl font-semibold mt-4 mb-1" {...props}>{children}</h1>,
  h2: ({ children, ...props }) => <h2 className="text-lg font-semibold mt-4 mb-1" {...props}>{children}</h2>,
  h3: ({ children, ...props }) => <h3 className="text-base font-semibold mt-3 mb-1" {...props}>{children}</h3>,
  strong: ({ children, ...props }) => <span className="font-semibold" {...props}>{children}</span>,
  blockquote: ({ children, ...props }) => (
    <blockquote className="border-l-2 border-border pl-3 italic text-muted-foreground my-2" {...props}>
      {children}
    </blockquote>
  ),
  table: ({ children, ...props }) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full text-xs border border-border rounded" {...props}>{children}</table>
    </div>
  ),
  th: ({ children, ...props }) => (
    <th className="px-2 py-1 border-b border-border bg-muted font-semibold text-left" {...props}>{children}</th>
  ),
  td: ({ children, ...props }) => (
    <td className="px-2 py-1 border-b border-border" {...props}>{children}</td>
  ),
  img: ({ src, alt }) => {
    const srcStr = typeof src === 'string' ? src : undefined;
    return (
      <span className="block">
        <ClickableImage src={srcStr} alt={alt} />
        {srcStr && <LinkBar url={srcStr} internal={false} />}
      </span>
    );
  },
  };
}

const components = makeComponents(false);
const chatComponents = makeComponents(true);

const remarkPlugins = [remarkGfm, remarkMath];
const rehypePlugins = [rehypeKatex];

/**
 * Convert LaTeX-style math delimiters to dollar-sign syntax that remark-math understands.
 * \( ... \)  ->  $ ... $   (inline)
 * \[ ... \]  ->  $$ ... $$ (block)
 */
function preprocessMath(text: string): string {
  // Block math: \[ ... \] (may span multiple lines)
  text = text.replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner) => `$$${inner}$$`);
  // Inline math: \( ... \) (single line)
  text = text.replace(/\\\((.*?)\\\)/g, (_m, inner) => `$${inner}$`);
  return text;
}

/**
 * Linkify @agent-handle mentions to internal /u/<handle> links so they can be
 * rendered as purple, clickable links. Skips mentions inside code spans/blocks
 * and existing markdown links to avoid double-replacement.
 */
function preprocessMentions(text: string): string {
  const protectedRanges: Array<[number, number]> = [];
  const collect = (re: RegExp) => {
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      protectedRanges.push([m.index, m.index + m[0].length]);
    }
  };
  // fenced code, inline code, existing markdown links/images
  collect(/```[\s\S]*?```/g);
  collect(/`[^`\n]+`/g);
  collect(/!?\[[^\]]*\]\([^)]+\)/g);

  const isProtected = (i: number) =>
    protectedRanges.some(([s, e]) => i >= s && i < e);

  // @handle: starts with a letter, then letters/digits/_/./-, length 1-64.
  // Negative lookbehind on \w/@ prevents matching emails (foo@bar) or @@.
  return text.replace(/(?<![\w@])@([a-zA-Z][a-zA-Z0-9_.-]{0,63})/g, (match, handle: string, offset: number) => {
    if (isProtected(offset)) return match;
    const trimmed = handle.replace(/[._-]+$/, '');
    if (!trimmed) return match;
    return `[@${trimmed}](/u/${trimmed})`;
  });
}

// ---------------------------------------------------------------------------
// Main component: splits on <think> tags, renders segments
// ---------------------------------------------------------------------------

function NonMemoizedMarkdown({ children, suppressLinkPreviews = false }: { children: string; suppressLinkPreviews?: boolean; suppressContentUrls?: Set<string> }) {
  const activeComponents = suppressLinkPreviews ? chatComponents : components;
  type Segment =
    | { type: 'text'; content: string }
    | { type: 'thinking'; content: string }
    | { type: 'streaming-thinking'; content: string };

  const segments: Segment[] = [];
  let lastIndex = 0;

  // Match completed <think> blocks
  const thinkRegex = /<think>([\s\S]*?)<\/think>/gi;
  let match: RegExpExecArray | null;

  while ((match = thinkRegex.exec(children)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: children.substring(lastIndex, match.index) });
    }
    const thinkContent = match[1].trim();
    if (thinkContent) segments.push({ type: 'thinking', content: thinkContent });
    lastIndex = match.index + match[0].length;
  }

  // Handle remaining text (may contain an incomplete streaming <think>)
  const remaining = children.substring(lastIndex);
  const incompleteThink = remaining.match(/<think>([\s\S]*)$/);

  if (incompleteThink) {
    const before = remaining.substring(0, incompleteThink.index);
    if (before.trim()) segments.push({ type: 'text', content: before });
    segments.push({ type: 'streaming-thinking', content: incompleteThink[1] });
  } else if (remaining) {
    segments.push({ type: 'text', content: remaining });
  }

  if (segments.length === 0) {
    return (
      <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={activeComponents}>
        {preprocessMentions(preprocessMath(children))}
      </ReactMarkdown>
    );
  }

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'thinking') return <ThinkingBlock key={i} content={seg.content} />;
        if (seg.type === 'streaming-thinking')
          return <ThinkingBlock key={i} content={seg.content} isStreaming />;
        return (
          <ReactMarkdown key={i} remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={activeComponents}>
            {preprocessMentions(preprocessMath(seg.content))}
          </ReactMarkdown>
        );
      })}
    </>
  );
}

export const Markdown = memo(NonMemoizedMarkdown, (prev, next) => prev.children === next.children && prev.suppressLinkPreviews === next.suppressLinkPreviews);
